import { CreatorAccessory } from '@/types/creator';
import { UUID } from '@/types/types';
import { Accessory } from '../objects/Accessory';
import { ProductRenderer } from '../ProductRenderer';
import { CollisionDetector } from '../utils/CollisionDetector';
import {
	AccessoryHostingSlot,
	AccessorySlot,
	AxisIndex,
	SerializedAccessoriesManager,
	ThreeEvent,
} from '../types';
import { getAxes } from '@/utils/utilities';
import { Vector3 } from 'three';

export class AccessoriesManager {
	private collisionDetector = new CollisionDetector(this);
	accessories: Accessory[] = [];

	constructor(private renderer: ProductRenderer) {}

	addAccessory(creatorAccessory: CreatorAccessory, skipCollisionCheck = false) {
		const object = this.renderer.productManager.getAccessoryObject(
			creatorAccessory.accessory.slug
		);

		const accessory = new Accessory(this.renderer, creatorAccessory, object);
		this.accessories.push(accessory);

		if (!skipCollisionCheck) this.collisionDetector.checkCollisions(accessory);

		const allHostingSlots = this.accessories.reduce<AccessoryHostingSlot[]>(
			(acc, accessory) => {
				return acc.concat(accessory.hostingSlots);
			},
			[]
		);

		for (const hostingSlot of allHostingSlots) {
			if (hostingSlot.childAccessoryName !== accessory.objectName) continue;
			this.handleHostingSlotChange(hostingSlot);
		}

		return accessory;
	}

	setAccessorySlot(id: UUID, slot: AccessorySlot) {
		for (const accessory of this.accessories) {
			if (accessory.id !== id) continue;

			accessory.selectedSlot = slot;
			this.collisionDetector.checkCollisions(accessory);
			break;
		}
	}

	setAccessoryAxisPosition(id: UUID, axis: AxisIndex, position: number) {
		for (const accessory of this.accessories) {
			if (accessory.id !== id) continue;

			accessory.setAxisPosition(axis, position);

			for (const hostingSlot of accessory.hostingSlots) {
				this.handleHostingSlotChange(hostingSlot);
			}

			this.collisionDetector.checkCollisions(accessory);
			break;
		}
	}

	removeAccessory(id: UUID) {
		for (const accessory of this.accessories) {
			if (accessory.id !== id) {
				accessory.removeCollidedAccessories(id);
				continue;
			}

			accessory.dispose();
		}

		this.accessories = this.accessories.filter(
			(accessory) => accessory.id !== id
		);

		this.syncCollisions();
	}

	clear() {
		for (const accessory of this.accessories) {
			accessory.dispose();
		}
		this.accessories = [];
	}

	export(): SerializedAccessoriesManager {
		return {
			accessories: this.accessories.map((accessory) => accessory.export()),
		};
	}

	syncCollisions() {
		this.renderer.emitter.emit(
			ThreeEvent.SET_ACCESSORIES_IS_COLLIDING,
			this.accessories.map((accessory) => ({
				refId: accessory.id,
				isColliding: accessory.isColliding,
			}))
		);
	}

	private handleHostingSlotChange(hostingSlot: AccessoryHostingSlot) {
		for (const accessory of this.accessories) {
			if (accessory.objectName !== hostingSlot.childAccessoryName) continue;

			hostingSlot.object.updateMatrixWorld(true);

			const hostWorldPos = hostingSlot.object.getWorldPosition(new Vector3());
			const axisName = getAxes()[hostingSlot.adjustAxis];

			//@ts-ignore
			const axisPos = hostWorldPos[axisName];

			accessory.setAxisPosition(hostingSlot.adjustAxis, axisPos);
		}
	}

	async load(
		data: SerializedAccessoriesManager,
		creatorAccessories: CreatorAccessory[]
	) {
		for (const accessoryData of data.accessories) {
			const creatorAccessory = creatorAccessories.find(
				(creaACc) => creaACc.refId === accessoryData.id
			);

			if (!creatorAccessory) throw new Error('CREATOR_ACCESSORY_NOT_FOUND');

			const accessory = this.addAccessory(creatorAccessory, true);
			accessory.load(accessoryData);
		}
	}
}
